import { Button, Field as ChakraField, HStack, Text, VStack } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import { InputNumber } from '@components/ui/input/InputNumber';
import { Switch } from '@components/ui/switch';
import { Field, getIn, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';

import { AlertingEventLevelEnum, Platform } from '@/types/config/config.types';

const LEVEL_OPTIONS = Object.values(AlertingEventLevelEnum);

export default function PlatformMessagingForm() {
  const { values, setFieldValue, touched, errors } = useFormikContext<Partial<Platform>>();

  const isInvalid = (key: string) => {
    const isTouched = getIn(touched, key);
    const error = getIn(errors, key);
    return Boolean(isTouched && error);
  };

  return (
    <CockpitFormLayout title="components.platforms.messaging.title" width="1088px">
      <HStack gap={6} width="100%" alignItems="start">
        <VStack gap={5} alignItems="start" flex={1}>
          <Text color="neutral.300">
            <FormattedMessage id="components.platforms.messaging.alertStart" />
          </Text>
          <VStack width="100%" gap={0.25} border="2px solid" borderColor="neutral.850" backgroundColor="neutral.850">
            <HStack gap={2} padding={4} width="100%" backgroundColor="neutral.700">
              <Text flex={1}>
                <FormattedMessage id="components.platforms.messaging.nfzEntry" />
              </Text>
              <Switch
                size="sm"
                checked={values.messagingParameters?.nfzEntry.enabled}
                onCheckedChange={({ checked }) => setFieldValue('messagingParameters.nfzEntry.enabled', checked)}
              />
            </HStack>
            {values.messagingParameters?.nfzEntry.enabled && (
              <VStack gap={2} padding={4} width="100%" backgroundColor="neutral.800" alignItems="start">
                <Text color="neutral.300">
                  <FormattedMessage id="components.platforms.messaging.levels" />
                </Text>
                <HStack gap={1} width="100%">
                  {LEVEL_OPTIONS.map((level) => {
                    const isActive = values.messagingParameters?.nfzEntry.levels.includes(level);
                    return (
                      <Button
                        key={level}
                        variant="toggle"
                        flex={1}
                        size="md"
                        data-active={isActive}
                        onClick={() => {
                          if (isActive) {
                            setFieldValue(
                              'messagingParameters.nfzEntry.levels',
                              values.messagingParameters?.nfzEntry.levels.filter((l) => l !== level),
                            );
                          } else {
                            setFieldValue(
                              'messagingParameters.nfzEntry.levels',
                              values.messagingParameters?.nfzEntry.levels.concat(level),
                            );
                          }
                        }}
                      >
                        <FormattedMessage id={`components.platforms.messaging.level.${level as string}`} />
                      </Button>
                    );
                  })}
                </HStack>
              </VStack>
            )}
          </VStack>
        </VStack>
        <VStack gap={5} alignItems="start" flex={1}>
          <Text color="neutral.300">
            <FormattedMessage id="components.platforms.messaging.alertEnd" />
          </Text>
          <VStack width="100%" gap={0.25} border="2px solid" borderColor="neutral.850" backgroundColor="neutral.850">
            <VStack width="100%" padding={4} gap={4} alignItems="start" backgroundColor="neutral.700">
              <HStack gap={2} width="100%">
                <Text flex={1}>
                  <FormattedMessage id="components.platforms.messaging.nfzExit" />
                </Text>
                <Switch
                  size="sm"
                  checked={values.messagingParameters?.nfzExit.enabled}
                  onCheckedChange={({ checked }) => setFieldValue('messagingParameters.nfzExit.enabled', checked)}
                />
              </HStack>
              {values.messagingParameters?.nfzExit.enabled && (
                <VStack gap={2} alignItems="start">
                  <Text color="neutral.300">
                    <FormattedMessage id="components.platforms.messaging.delay" />
                  </Text>
                  <ChakraField.Root variant="cockpit" required invalid={isInvalid('messagingParameters.nfzExit.delay')}>
                    <Field
                      as={InputNumber}
                      type="text"
                      width="102px"
                      name="messagingParameters.nfzExit.delay"
                      id="messagingParameters.nfzExit.delay"
                      allowMouseWheel
                      onChange={(value?: number) => setFieldValue('messagingParameters.nfzExit.delay', value)}
                      suffix="min"
                    />
                  </ChakraField.Root>
                </VStack>
              )}
            </VStack>
            {values.messagingParameters?.nfzExit.enabled && (
              <VStack gap={2} padding={4} width="100%" backgroundColor="neutral.800" alignItems="start">
                <Text color="neutral.300">
                  <FormattedMessage id="components.platforms.messaging.levels" />
                </Text>
                <HStack gap={1} width="100%">
                  {LEVEL_OPTIONS.map((level) => {
                    const isActive = values.messagingParameters?.nfzExit.levels.includes(level);
                    return (
                      <Button
                        key={level}
                        variant="toggle"
                        flex={1}
                        size="md"
                        data-active={isActive}
                        onClick={() => {
                          if (isActive) {
                            setFieldValue(
                              'messagingParameters.nfzExit.levels',
                              values.messagingParameters?.nfzExit.levels.filter((l) => l !== level),
                            );
                          } else {
                            setFieldValue(
                              'messagingParameters.nfzExit.levels',
                              values.messagingParameters?.nfzExit.levels.concat(level),
                            );
                          }
                        }}
                      >
                        <FormattedMessage id={`components.platforms.messaging.level.${level as string}`} />
                      </Button>
                    );
                  })}
                </HStack>
              </VStack>
            )}
          </VStack>
          <HStack
            width="100%"
            padding={4}
            gap={2}
            border="2px solid"
            borderColor="neutral.850"
            backgroundColor="neutral.700"
          >
            <Text flex={1}>
              <FormattedMessage id="components.platforms.messaging.droneLost" />
            </Text>
            <Switch
              size="sm"
              checked={values.messagingParameters?.droneLost.enabled}
              onCheckedChange={({ checked }) => setFieldValue('messagingParameters.droneLost.enabled', checked)}
            />
          </HStack>
        </VStack>
      </HStack>
    </CockpitFormLayout>
  );
}
